import React from 'react';
import {Button, Empty, Flex, Popconfirm, Space} from 'antd';
import {MinusCircleOutlined, PlusOutlined} from '@ant-design/icons';
import VirtualizedList from 'rc-virtual-list';
import AgentName from '@/components/Agent/AgentName';
import LeadAssigneeDropdownFilters from '@/components/Filters/LeadAssigneeDropdownFilters';
import {AssigneeModel} from '@/typings/models/Assignee';
import styles from './index.less';
import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import classNames from 'classnames';

interface VirtualUserListProps {
  users: AssigneeModel[];
  onAdd: (userId: string, user?: AssigneeModel) => void;
  onRemove: (userId: string) => void;
  placeholder?: any;
  height?: number;
  itemHeight?: number;
}

const VirtualUserList: React.FC<VirtualUserListProps> = ({
  users = [],
  onAdd,
  onRemove,
  placeholder,
  height = 300,
  itemHeight = 40,
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const processedUsers = React.useMemo(() => {
    const seen = new Set();
    return (users || []).filter((u) => {
      const id = typeof u === 'string' ? u : u?.id || (u as any)?.alias;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [users]);

  const currentIds = processedUsers
    .map((u) => (typeof u === 'string' ? u : u.id || (u as any).alias))
    .filter(Boolean);

  const addLabel = placeholder || (
    <Space>
      <PlusOutlined />
      <F id={'entity.user.singular'} />
    </Space>
  );

  return (
    <div className={styles.userListContainer}>
      {processedUsers.length > 0 ? (
        <VirtualizedList
          data={processedUsers}
          height={Math.min(height, processedUsers.length * itemHeight)}
          itemHeight={itemHeight}
          itemKey={(item) =>
            typeof item === 'string' ? item : item.id || (item as any).alias
          }
        >
          {(user) => {
            const userId =
              typeof user === 'string' ? user : user.id || (user as any).alias;
            return (
              <div className={styles.userItem} key={userId}>
                <Flex justify={'space-between'} align={'center'}>
                  {typeof user === 'string' ? (
                    userId
                  ) : (
                    <AgentName clickable agent={user} />
                  )}
                  <Popconfirm
                    title={
                      <F
                        entity={INTL_ENTITY.USER}
                        action={INTL_ACTION.REMOVE_CONFIRMATION}
                      />
                    }
                    onConfirm={() => onRemove(userId)}
                  >
                    <Button
                      size={'small'}
                      icon={<MinusCircleOutlined />}
                      type={'text'}
                      danger
                    />
                  </Popconfirm>
                </Flex>
              </div>
            );
          }}
        </VirtualizedList>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} styles={{
          image: {height:25},
          root: {margin: 15}
        }}/>
      )}
      <div className={styles.addUserSection}>
        <LeadAssigneeDropdownFilters
          hideDisabled
          showCreate
          type="dropdown"
          mode="multiple"
          filterMode={'select'}
          value={currentIds}
          placeholder={isFocused ? null : addLabel}
          className={classNames(styles.buttonSelect, {
            [styles.buttonFocused]: isFocused,
          })}
          onChange={(ids, newUsers) => {
            const newIds = [...new Set((Array.isArray(ids) ? ids : [ids]) as string[])];

            // Find added
            newIds.forEach((id) => {
              if (!currentIds.includes(id)) {
                const user = (newUsers as AssigneeModel[]).find(
                  (u) => (u.id || (u as any).alias) === id,
                );
                onAdd(id, user);
              }
            });

            // Find removed
            currentIds.forEach((id) => {
              if (!newIds.includes(id)) {
                onRemove(id);
              }
            });
          }}
          selectProps={{
            size: 'middle',
            style: { width: '100%' },
            showSearch: true,
            allowClear: false,
            maxTagCount: 0,
            maxTagPlaceholder: isFocused ? () => null : () => addLabel,
            onFocus: () => setIsFocused(true),
            onBlur: () => setIsFocused(false),
            bordered: false,
            showArrow: false,
          }}
        />
      </div>
    </div>
  );
};

export default VirtualUserList;
